/**
 * Referral link capture.
 *
 * A referral link looks like `/pricing?checkout=true&ref=CATCH-XXXX`. The friend
 * usually needs to sign in (Google OAuth) before checkout, which leaves and
 * re-enters the SPA in the same tab. We persist the code in localStorage (which
 * survives the full OAuth round-trip) and the auto-open intent in sessionStorage
 * (this browser session). The discount itself is enforced server-side at
 * checkout, so storage is only a convenience for re-applying it after login.
 *
 * Also captures the *referrer* side: `?referral_link=1` (the promo page's
 * "Get your referral link" CTA) marks an intent to spotlight the user's own
 * link on the Integrations page, surviving the same login round-trip.
 */

const REFERRAL_CODE_KEY = 'catch_referral_code'
const CHECKOUT_INTENT_KEY = 'catch_checkout_intent'
const SPOTLIGHT_INTENT_KEY = 'catch_referral_spotlight_intent'
const AMBASSADOR_ENTRY_KEY = 'catch_refer_ambassador_entry'

/** The `/refer` target the ambassador page links to. The marker travels with
 * the link so a shared or pasted URL carries the entry too, not just an in-app
 * click. */
export const REFER_FROM_AMBASSADORS_PATH = '/refer?from=ambassadors'

// Loose format gate. The canonical format lives where codes are minted
// (backend/src/app/services/referral_service.py); the same check is inlined in
// landing/src/pages/Our-biggest-referral-reward-yet.astro. The discount itself
// is enforced server-side at checkout.
const REFERRAL_CODE_PATTERN = /^CATCH-[A-Z0-9]{4,16}$/

/** Normalize a raw referral code (trim + uppercase) and return it only if it
 * matches the canonical format, else null. The backend lookup is case-sensitive
 * and codes are minted uppercase, so this keeps storage, checkout, analytics,
 * and the signup banner on one canonical value and rejects junk (template
 * placeholders, full URLs, legacy values). Shared so every capture point gates
 * identically. */
export function normalizeReferralCode(raw: string | null | undefined): string | null {
  if (!raw) {
    return null
  }
  const code = raw.trim().toUpperCase()
  return REFERRAL_CODE_PATTERN.test(code) ? code : null
}

/** Stash `?ref=`, `?checkout=true` and `?referral_link=1` from the current URL
 * into web storage. Codes are normalized (trimmed, uppercased — the backend
 * lookup is case-sensitive) and format-gated before storage, so checkout,
 * analytics, and the signup banner all see one canonical value. */
export function captureReferralFromUrl(): void {
  const params = new URLSearchParams(window.location.search)
  const ref = normalizeReferralCode(params.get('ref'))
  if (ref) {
    localStorage.setItem(REFERRAL_CODE_KEY, ref)
  }
  if (params.get('checkout') === 'true') {
    sessionStorage.setItem(CHECKOUT_INTENT_KEY, 'true')
  }
  if (params.get('referral_link') === '1') {
    sessionStorage.setItem(SPOTLIGHT_INTENT_KEY, 'true')
  }
}

export function getStoredReferralCode(): string | null {
  return localStorage.getItem(REFERRAL_CODE_KEY)
}

/** The stored code if it looks like a real referral code, else null. Gates
 * referral UI (e.g. the signup banner) against legacy values stored before
 * `captureReferralFromUrl` normalized and format-gated at capture time. */
export function getValidStoredReferralCode(): string | null {
  return normalizeReferralCode(getStoredReferralCode())
}

/** Drop the stored code once it's served its purpose (e.g. after subscribing),
 * so it isn't re-sent on the user's own later checkouts. */
export function clearStoredReferralCode(): void {
  localStorage.removeItem(REFERRAL_CODE_KEY)
}

/** The subscription facts that decide whether a stored referral code can still
 * be spent. A structural input rather than the whole `SubscriptionResponse` so
 * the rule below is pure and testable without standing up a billing fixture. */
export interface ReferralSpendInputs {
  /** The user's own Stripe subscription id, if they have one. */
  stripeSubscriptionId?: string | null | undefined
}

/**
 * Whether a stored referral code has already had its one chance to apply.
 *
 * The discount is applied by `routers/billing.py`'s checkout, which resolves a
 * code only while the user has **no Stripe subscription of their own** — so that
 * is the one state in which the code is genuinely spent. Mirroring the backend's
 * own test exactly, rather than approximating it, is the point: the two cannot
 * then disagree about when the code dies.
 *
 * Team billing looks like a second such state and is not, in either of its two
 * available forms:
 *
 * - `billing_managed_by_team` reports org **membership**, not coverage —
 *   `get_subscription_status` says so in as many words ("this must never gate a
 *   charge") — and since GET-9209 ungated domain auto-join, a business signup
 *   can be a member of an org that pays for nobody. Those users do reach
 *   checkout and do pay, so spending their code on that flag would be this same
 *   bug wearing a different proxy.
 * - `utils/billing.ts::isTeamCovered` IS the correct "a team really pays for
 *   this seat" test, and was considered here. It is still not this decision: a
 *   covered seat makes the code **dormant, not spent**. That member can leave
 *   the org, or the org can stop paying, and then they check out personally —
 *   having silently lost the discount. The only state from which there is no
 *   way back is a Stripe subscription of their own.
 *
 * What that costs: a genuinely team-covered member keeps a code they cannot use
 * today, so the login page's "A friend has you covered" banner can resurface
 * for them (the GET-7054 symptom, narrowed to that cohort). Cosmetic, weighed
 * against deleting a payer's discount — the same trade this whole fix turns on.
 *
 * Deliberately NOT `is_active` (GET-12622). That was the original test, on the
 * reasoning that active access implies the user has already been through
 * checkout — true when the only way to get access was to enter a card, and
 * false ever since the cardless trial. A business-email signup is granted a
 * `trialing` row with a NULL `stripe_subscription_id` **inside the mail/calendar
 * OAuth callback**, i.e. during onboarding and a few minutes before the billing
 * step; measured on 2026-08-22, 64 of the 75 users who checked out after such a
 * grant did so within 10 minutes of it. Reading `is_active` there deleted the
 * code between the grant and the checkout it was meant for, so every referred
 * signup silently lost the 50%-off first month, the referrer's reward and the
 * ambassador invitee's credit. Zero referrals applied in the 37 days before that
 * was found.
 */
export function referralCodeIsSpent({
  stripeSubscriptionId,
}: ReferralSpendInputs): boolean {
  return Boolean(stripeSubscriptionId)
}

export function hasCheckoutIntent(): boolean {
  return sessionStorage.getItem(CHECKOUT_INTENT_KEY) === 'true'
}

export function clearCheckoutIntent(): void {
  sessionStorage.removeItem(CHECKOUT_INTENT_KEY)
}

/** The referral promo page sends existing users to
 * `/integrations?referral_link=1` so the app spotlights their personal link.
 * Logged-out visitors lose the query string on the login redirect, so the
 * intent is stashed for this browser session and honored when the
 * Integrations page next mounts. */
export function hasReferralSpotlightIntent(): boolean {
  return sessionStorage.getItem(SPOTLIGHT_INTENT_KEY) === 'true'
}

export function clearReferralSpotlightIntent(): void {
  sessionStorage.removeItem(SPOTLIGHT_INTENT_KEY)
}

/** Stash `?from=ambassadors` — the marker the ambassador page's "Open your
 * Refer page" CTA carries — for this browser session.
 *
 * Same round-trip problem as the spotlight intent above, twice over: a
 * logged-out visitor loses the query string on the login redirect, and any
 * later in-app hop back to a bare `/refer` has no params either. Called from
 * `ReferralRoute`, which is the one place both a direct URL and an in-app
 * navigation pass through, and which runs before the auth redirect. */
export function captureAmbassadorEntry(search: string): void {
  if (new URLSearchParams(search).get('from') === 'ambassadors') {
    sessionStorage.setItem(AMBASSADOR_ENTRY_KEY, 'true')
  }
}

/** Whether this session reached /refer from the ambassador offer page. Gates
 * the offer reminder on /refer so a private, approval-only offer isn't
 * advertised to every user who opens their refer page. Not access control:
 * `/ambassadors` is a public route, so anyone who opens it gets the marker. */
export function hasAmbassadorEntry(): boolean {
  return sessionStorage.getItem(AMBASSADOR_ENTRY_KEY) === 'true'
}
